import React, { useState, useCallback, useMemo } from 'react';
import { ExecutionTemplate, Icon } from 'process-pipelines-common';
import { GridControl, FormThemeProvider } from '@atlas-forms/player-components-react';
import type { GridConfig, GridRow } from '@atlas-forms/player-components-react';

export interface DataItemsTabProps {
  template: ExecutionTemplate;
  onChange: (updates: Partial<ExecutionTemplate>) => void;
  readOnly?: boolean;
}

export const DataItemsTab: React.FC<DataItemsTabProps> = ({
  template,
  onChange,
  readOnly = false,
}) => {
  const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'json'>('grid');
  const [jsonText, setJsonText] = useState<string>('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Debounce refs to prevent re-renders during editing
  const updateTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const pendingChangesRef = React.useRef<any>(null);

  const config = useMemo(() => {
    try {
      return JSON.parse(template.configuration || '{"inputData":{"items":[]}}');
    } catch {
      return { inputData: { items: [] } };
    }
  }, [template.configuration]);

  const items = useMemo(() => {
    return config.inputData?.items || [];
  }, [config]);

  const selectedItem = selectedItemIndex !== null ? items[selectedItemIndex] : null;

  // Initialize JSON view when switching to it
  const initializeJsonView = useCallback(() => {
    if (selectedItem) {
      try {
        setJsonText(JSON.stringify(selectedItem, null, 2));
        setJsonError(null);
      } catch (e) {
        setJsonError('Failed to serialize item to JSON');
      }
    }
  }, [selectedItem]);

  // Handle JSON changes
  const handleJsonChange = useCallback((text: string) => {
    setJsonText(text);
    // Validate JSON as user types
    try {
      JSON.parse(text);
      setJsonError(null);
    } catch (e) {
      setJsonError('Invalid JSON: ' + (e instanceof Error ? e.message : 'Unknown error'));
    }
  }, []);

  // Apply JSON changes
  const handleJsonApply = useCallback(() => {
    if (selectedItemIndex === null) return;
    try {
      const parsed = JSON.parse(jsonText);
      const newItems = [...items];
      newItems[selectedItemIndex] = parsed;
      const updated = {
        ...config,
        inputData: { ...config.inputData, items: newItems },
      };
      onChange({ configuration: JSON.stringify(updated) });
      setJsonError(null);
    } catch (e) {
      setJsonError('Failed to apply JSON: ' + (e instanceof Error ? e.message : 'Unknown error'));
    }
  }, [selectedItemIndex, items, config, onChange, jsonText]);

  // Format JSON
  const handleJsonFormat = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonText);
      setJsonText(JSON.stringify(parsed, null, 2));
      setJsonError(null);
    } catch (e) {
      setJsonError('Cannot format invalid JSON');
    }
  }, [jsonText]);

  // Convert selected item properties to grid rows format
  // Each property of the item becomes a row in the grid
  const propertyRows = useMemo(() => {
    if (!selectedItem) return [];
    return Object.entries(selectedItem).map(([propName, value]) => ({
      property: propName,
      type: typeof value === 'number' ? 'number' : typeof value === 'boolean' ? 'boolean' : 'text',
      value: (value === null || value === undefined) ? '' : String(value),
    }));
  }, [selectedItem]);

  // Grid configuration - Aligned with working reference schema
  const gridConfig: GridConfig = useMemo(
    () => ({
      columns: [
        {
          key: 'property',
          label: 'Property',
          type: 'text',
          required: true,
          helpText: 'Property name',
        },
        {
          key: 'type',
          label: 'Type',
          type: 'select',
          required: true,
          options: [
            { value: 'text', label: 'Text' },
            { value: 'number', label: 'Number' },
            { value: 'boolean', label: 'Boolean' },
            { value: 'date', label: 'Date' },
          ],
          helpText: 'Data type',
        },
        {
          key: 'value',
          label: 'Value',
          type: 'text',
          required: false,
          helpText: 'Property value',
        },
      ],
      bulkEditEnabled: true,
      selectionEnabled: true,
      emptyMessage: 'No properties. Click "Add Row" to add one.',
      newEntityTemplate: {
        property: '',
        type: 'text',
        value: '',
      },
      buttons: [
        {
          label: 'Delete',
          buttonAction: 'deleteSelected',
          requiresSelection: true,
          icon: 'trash',
        },
        {
          label: 'Add Row',
          buttonAction: 'add',
          icon: 'plus',
        },
        {
          label: 'Edit',
          buttonAction: 'editSelected',
          requiresSelection: true,
          selectionLimit: 1,
          icon: 'edit',
        },
      ],
    }),
    []
  );

  // Handle grid changes - update selected item properties with debouncing
  const handleGridChange = useCallback(
    (newRows: GridRow[] | Record<string, any>[]) => {
      if (selectedItemIndex === null) return;

      const selectedObj: Record<string, any> = {};

      // Convert grid rows back to item object
      (newRows as any[]).forEach((row: any) => {
        const propName = row.property;
        let value: any = row.value;

        // Convert value to correct type based on row.type
        if (row.type === 'number') {
          value = value === '' ? 0 : Number(value) || 0;
        } else if (row.type === 'boolean') {
          value = value === 'true' || value === true;
        } else {
          value = String(value || '');
        }

        if (propName) {
          selectedObj[propName] = value;
        }
      });

      // Store pending changes
      pendingChangesRef.current = { selectedObj, selectedItemIndex };

      // Clear existing timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      // Debounce the onChange call (500ms) to prevent excessive re-renders
      updateTimeoutRef.current = setTimeout(() => {
        if (!pendingChangesRef.current) return;

        const { selectedObj: obj, selectedItemIndex: idx } = pendingChangesRef.current;
        const newItems = [...items];
        newItems[idx] = obj;

        const updated = {
          ...config,
          inputData: { ...config.inputData, items: newItems },
        };
        onChange({ configuration: JSON.stringify(updated) });
      }, 500);
    },
    [selectedItemIndex, items, config, onChange]
  );

  // Cleanup timeout on unmount or item change
  React.useEffect(() => {
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
    };
  }, [selectedItemIndex]);

  // Handle adding new item
  const handleAddItem = useCallback(() => {
    const template_skeleton = config.templates?.item || {};
    const newItems = [...items, { ...template_skeleton }];
    const updated = {
      ...config,
      inputData: { ...config.inputData, items: newItems },
    };
    onChange({ configuration: JSON.stringify(updated) });
    setSelectedItemIndex(newItems.length - 1);
  }, [items, config, onChange]);

  // Handle deleting item
  const handleDeleteItem = useCallback(
    (index: number) => {
      const newItems = items.filter((_: any, i: number) => i !== index);
      const updated = {
        ...config,
        inputData: { ...config.inputData, items: newItems },
      };
      onChange({ configuration: JSON.stringify(updated) });
      setSelectedItemIndex(null);
    },
    [items, config, onChange]
  );

  // Handle duplicating item
  const handleDuplicateItem = useCallback(
    (index: number) => {
      const item = items[index];
      const newItems = [...items, JSON.parse(JSON.stringify(item))];
      const updated = {
        ...config,
        inputData: { ...config.inputData, items: newItems },
      };
      onChange({ configuration: JSON.stringify(updated) });
      setSelectedItemIndex(newItems.length - 1);
    },
    [items, config, onChange]
  );

  // Handle reordering items
  const handleReorderItem = useCallback(
    (fromIndex: number, toIndex: number) => {
      const newItems = [...items];
      const [item] = newItems.splice(fromIndex, 1);
      newItems.splice(toIndex, 0, item);
      const updated = {
        ...config,
        inputData: { ...config.inputData, items: newItems },
      };
      onChange({ configuration: JSON.stringify(updated) });
    },
    [items, config, onChange]
  );

  return (
    <div style={styles.container}>
      {/* LEFT PANEL - Items List */}
      <div style={styles.leftPanel}>
        <div style={styles.panelHeader}>
          <h4 style={styles.panelTitle}>Items ({items.length})</h4>
          <button
            onClick={handleAddItem}
            style={styles.addButton}
            disabled={readOnly}
            title="Add new item"
          >
            + Add
          </button>
        </div>

        <div style={styles.itemsList}>
          {items.map((item: any, index: number) => (
            <div
              key={index}
              style={{
                ...styles.itemCard,
                ...(selectedItemIndex === index ? styles.itemCardSelected : {}),
              }}
            >
              <div
                onClick={() => setSelectedItemIndex(index)}
                style={styles.itemCardContent}
              >
                <strong>Item {index + 1}</strong>
                <p style={styles.itemPreview}>
                  {Object.keys(item).length} properties
                </p>
              </div>

              <div style={styles.itemActions}>
                <button
                  onClick={() => handleDuplicateItem(index)}
                  style={styles.miniButton}
                  title="Duplicate"
                  disabled={readOnly}
                >
                  <Icon icon="Clipboard" size={12} />
                </button>
                <button
                  onClick={() => {
                    if (index > 0) handleReorderItem(index, index - 1);
                  }}
                  style={styles.miniButton}
                  title="Move up"
                  disabled={readOnly || index === 0}
                >
                  <Icon icon="ChevronUp" size={12} />
                </button>
                <button
                  onClick={() => {
                    if (index < items.length - 1) handleReorderItem(index, index + 1);
                  }}
                  style={styles.miniButton}
                  title="Move down"
                  disabled={readOnly || index === items.length - 1}
                >
                  <Icon icon="ChevronDown" size={12} />
                </button>
                <button
                  onClick={() => handleDeleteItem(index)}
                  style={styles.deleteButton}
                  title="Delete"
                  disabled={readOnly}
                >
                  <Icon icon="Delete" size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT PANEL - Property Grid or JSON for Selected Item */}
      <div style={styles.rightPanel}>
        {selectedItem ? (
          <>
            {/* View Mode Toggle */}
            <div style={styles.viewToggle}>
              <button
                onClick={() => {
                  setViewMode('grid');
                }}
                style={{
                  ...styles.viewButton,
                  ...(viewMode === 'grid' ? styles.viewButtonActive : {}),
                }}
              >
                Grid View
              </button>
              <button
                onClick={() => {
                  setViewMode('json');
                  initializeJsonView();
                }}
                style={{
                  ...styles.viewButton,
                  ...(viewMode === 'json' ? styles.viewButtonActive : {}),
                }}
              >
                JSON View
              </button>
            </div>

            {/* Grid View */}
            {viewMode === 'grid' ? (
              <FormThemeProvider theme="light">
                <div style={styles.gridContainer}>
                  <div style={styles.gridHeader}>
                    <h4 style={styles.gridTitle}>
                      Item {(selectedItemIndex ?? 0) + 1} Properties
                    </h4>
                  </div>

                  {/* Atlas Forms GridControl showing properties as rows */}
                  <div style={styles.gridSection}>
                    <React.Suspense fallback={<div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>Loading grid…</div>}>
                      <GridControl
                        control={{ config: gridConfig } as any}
                        value={propertyRows}
                        onChange={handleGridChange}
                        onBlur={() => {}}
                        mode="edit"
                      />
                    </React.Suspense>
                  </div>
                </div>
              </FormThemeProvider>
            ) : (
              /* JSON View */
              <div style={styles.jsonContainer}>
                <div style={styles.jsonHeader}>
                  <h4 style={styles.gridTitle}>
                    Item {(selectedItemIndex ?? 0) + 1} JSON
                  </h4>
                </div>

                {jsonError && (
                  <div style={styles.jsonError}>
                    <strong>Error:</strong> {jsonError}
                  </div>
                )}

                <textarea
                  value={jsonText}
                  onChange={(e) => handleJsonChange(e.target.value)}
                  style={styles.jsonTextarea}
                  placeholder="Enter JSON here..."
                />

                <div style={styles.jsonActions}>
                  <button onClick={handleJsonFormat} style={styles.actionButton} title="Format JSON">
                    Format
                  </button>
                  <button
                    onClick={() => {
                      if (selectedItem) {
                        setJsonText(JSON.stringify(selectedItem, null, 2));
                        setJsonError(null);
                      }
                    }}
                    style={styles.actionButton}
                    title="Reset to saved value"
                  >
                    Reset
                  </button>
                  <button
                    onClick={handleJsonApply}
                    style={{
                      ...styles.actionButton,
                      ...styles.applyButton,
                    }}
                    title="Apply JSON changes"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={styles.emptyMessage}>
            <p>Select an item from the left panel to view and edit its properties</p>
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    height: '100%',
    gap: '12px',
    padding: '12px',
  },
  leftPanel: {
    flex: '0 0 280px',
    display: 'flex',
    flexDirection: 'column' as const,
    border: '1px solid #ddd',
    borderRadius: '4px',
    overflow: 'hidden',
    backgroundColor: '#fff',
  },
  rightPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column' as const,
    border: '1px solid #ddd',
    borderRadius: '4px',
    overflow: 'hidden',
    backgroundColor: '#fff',
  },
  panelHeader: {
    padding: '12px',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '12px',
  },
  panelTitle: {
    margin: 0,
    fontSize: '13px',
    fontWeight: '600',
  },
  addButton: {
    padding: '5px 12px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '11px',
    fontWeight: '600',
  },
  itemsList: {
    flex: 1,
    overflow: 'auto',
    padding: '8px',
  },
  itemCard: {
    padding: '8px',
    marginBottom: '6px',
    backgroundColor: '#f9f9f9',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  itemCardSelected: {
    backgroundColor: '#e8f5e9',
    borderColor: '#4CAF50',
    borderWidth: '2px',
  },
  itemCardContent: {
    marginBottom: '6px',
  },
  itemPreview: {
    margin: '4px 0 0 0',
    fontSize: '11px',
    color: '#666',
  },
  itemActions: {
    display: 'flex',
    gap: '2px',
  },
  miniButton: {
    flex: 1,
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f0f0f0',
    color: '#666',
    border: '1px solid #ccc',
    borderRadius: '2px',
    cursor: 'pointer',
    fontSize: '10px',
  },
  deleteButton: {
    flex: 1,
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#ffebee',
    color: '#c33',
    border: '1px solid #f5c6cb',
    borderRadius: '2px',
    cursor: 'pointer',
    fontSize: '10px',
  },
  gridContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    overflow: 'hidden',
  },
  gridHeader: {
    padding: '12px',
    borderBottom: '1px solid #e0e0e0',
  },
  gridTitle: {
    margin: 0,
    fontSize: '13px',
    fontWeight: '600',
  },
  gridSection: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
  },
  emptyMessage: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: '#999',
    fontSize: '14px',
    textAlign: 'center' as const,
  },
  viewToggle: {
    display: 'flex',
    gap: '8px',
    padding: '12px',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#f9f9f9',
  },
  viewButton: {
    padding: '6px 12px',
    backgroundColor: '#f0f0f0',
    color: '#666',
    border: '1px solid #ddd',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500',
  },
  viewButtonActive: {
    backgroundColor: '#4CAF50',
    color: 'white',
    borderColor: '#4CAF50',
  },
  jsonContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    overflow: 'hidden',
  },
  jsonHeader: {
    padding: '12px',
    borderBottom: '1px solid #e0e0e0',
  },
  jsonTextarea: {
    flex: 1,
    padding: '12px',
    border: 'none',
    borderTop: '1px solid #e0e0e0',
    fontFamily: 'monospace',
    fontSize: '12px',
    resize: 'none' as const,
    overflow: 'auto' as const,
  },
  jsonError: {
    padding: '8px 12px',
    backgroundColor: '#ffebee',
    color: '#c33',
    fontSize: '12px',
    borderBottom: '1px solid #f5c6cb',
  },
  jsonActions: {
    display: 'flex',
    gap: '8px',
    padding: '12px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#f9f9f9',
  },
  actionButton: {
    padding: '6px 12px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '3px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500',
  },
  applyButton: {
    backgroundColor: '#4CAF50',
  },
} as const;
